import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  isActionAllowed?: boolean;
  level3QueueAt?: number | null;
  level3SubmissionDate?: number | null;
};

export type TApplicationOverride = {
  id: number;
  application_id: string;
  override_by: string;
  reason: string;
  updated_date: string;
  user_id: string;
};

export const level3ApplicationsKeyStore = {
  savedDemandNoteData: (...args: (number | string | boolean | undefined)[]) => [
    "level-3-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-3-application-last-review", id],
  override: (id?: number | string, type?: string) => ["level-3-applicationId", id, type],
  allReviews: (id?: number | string) => ["level-3-application-all-reviews", id],
  id: (arg: number | string) => ["level-3-application", arg],
  decisionMaps: (arg?: number | string) => ["level-3-decision-maps", arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-3-applications-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-3-applications",
    ...args,
  ],
  stats: (...args: (number | string | Record<string, unknown>)[]) => [
    "level-3-applications-stats",
    ...args,
  ],
  autoFill: () => ["auto-fill"],
};

export function useApplicationStatsForLevel3({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/level-3-application-approval/stats", { filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationCountForLevel3({
  search,
  filters,
}: {
  search?: string;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-3-application-approval/count", { search, filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationsForLevel3({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TApplication[];
      }>("/level-3-application-approval/list", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationForLevel3({ id }: { id: number | string }) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/level-3-application-approval/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useApplicationOverride(applicationId: string | undefined, overrideType: 'application' | 'payment' = 'application') {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.override(applicationId, overrideType),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: TApplicationOverride & Record<string, unknown>;
      }>("/override-application", {
        application_id: applicationId, override_type: overrideType
      }),
    enabled: !!applicationId,
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useLevel3DecisionMaps({ id }: { id: undefined | number }) {
  return useQuery({
    enabled: !!id,
    queryKey: level3ApplicationsKeyStore.decisionMaps(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          nocDecisions: Record<string, boolean>;
          documentDecisions: Record<string, boolean>;
        };
      }>(`/level-3-application-approval/decision-map/${id}`),
    select: (res) => res?.data?.data,
  });
}

export function useLevel3UpdateDocumentDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      docId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-3-application-approval/update-doc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel3UpdateNocDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      nocId: number;
      decision: 0 | 1;
      applicationId: number;
    }) =>
      _axios.post("/level-3-application-approval/update-noc-decision", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.decisionMaps(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel3LastReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          fieldsToChange?: number[];
          review?: { comment_text?: string; id?: number };
        };
      }>(`/level-3-application-approval/get-current-review/${id}`),
    select: (result) => result.data?.data,
  });
}

export type TReviewWithReviewer = {
  id: number;
  date: string;
  comment_text: string;
  level: string;
  reviewer?: { name: string; role: string };
  status: string;
};

export function useLevel3AllReviews({ id }: { id?: number }) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.allReviews(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>(`/level-3-application-approval/get-all-reviews/${id}`),
    select: (result) => result.data?.data,
  });
}

export function useSaveLevel3Review() {
  const qc = useQueryClient();
  //debugger;
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      comment: string;
      fieldsToChange: number[];
    }) => _axios.post("/level-3-application-approval/save-review", data),

    onSuccess: () => SuccessToast("Saved!"),
    onError: () => ErrorToast("Something went wrong while saving review!"),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.lastReview(applicationId),
      });
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.allReviews(applicationId),
      });
    },
  });
}

export function useLevel3RejectApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      application_id: string;
      user_id: number;
    }) => _axios.post("/level-3-application-approval/reject-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.counts() });
    },
    onSuccess: () => SuccessToast("Application rejected successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while rejecting application!"),
  });
}

export function useLevel3ReturnApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      application_id: string;
      user_id: number;
    }) => _axios.post("/level-3-application-approval/return-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.counts() });
    },
    onSuccess: () => SuccessToast("Application returned successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while returning application!"),
  });
}

export type TSavedDemandNoteData = {
  municipality?: string;
  tehsil?: string;
  district?: string;
  dueDate?: string;
  applicantName?: string;
  layoutName?: string;
  baseAmount?: number;
  interestAccumulated?: number;
  daysPending?: number;
  area?: number;
  unit_area?: string;
  plotUsageType?: string;
  mauja?: string;
  khasaraNo?: string;
  main_khasara_no?: string;
  manual_khasara_no?: string;
  constructionArea?: number;
  particular1Rate?: number;
  particular1Total?: number;
  particular2Rate?: number;
  particular2Total?: number;
  supervisionTotal?: number;
  supervisionRate?: number;
  totalAmount?: number;
  supervisionCharges?: number;
  supervision_charges?: number;
  compoundingCharges?: number;
  swmCharges?: number;
  administrativeCharges?: number;
  surveyMeasurementCharges?: number;
  infrastructureFee?: number;
  areaOfPlot?: number;
  plotNo?: string;
  scrutinyCharges?: number;
  conditions?: string;
  buildingDevelopmentRRLandRate?: number;
  buildingDevelopmentRRLandTotal?: number;
  labourCessRRConstructionRate?: number;
  labourCessRRConstructionTotal?: number;
  chargesForConstructioInSideMargineRRLandRate?: number;
  chargesForConstructioInSideMargineRRLandTotal?: number;
  areaExcessOfBasicFSI?: number;
  rainWaterHarwesting?: number;
  securityDeposite?: number;
  rrLandRate?: number;
  regularizationPercent?: number;
  developmentPercent?: number;
  chargesForExcessBuiltUpArea?: number;
  percentRRLandRateForExcessBuiltUpArea?: number;
  supervisionChargesForOpenPlot?: number;
  supervisionChargesForConstruction?: number;
  areaOfDemolish?: number;
  areaOfExcessBuiltUp?: number;
  excessBuiltUpArea?: number;
};

interface AutoFillFieldsState {
  municipal_council_name: string;
  tahasil_name: string;
  district_name: string;
  demand_note_logo: string;
  demand_note_header_text: string;
  certificate_header_text: string;
  certificate_logo: string;
}

export function useAutoFillDemandNoteData() {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.autoFill(),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{ success: true; data?: AutoFillFieldsState[] }>(
        "/controller/auto-fill"
      ),
  });
}

export function useSavedDemandNoteDataForLevel3({
  applicationId,
  reviewId,
  isThereAConst,
}: {
  applicationId?: number;
  reviewId?: number;
  isThereAConst?: boolean;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.savedDemandNoteData(
      applicationId,
      reviewId,
      isThereAConst
    ),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{ success: true; data?: TSavedDemandNoteData }>(
        "/level-3-application-approval/get-saved-demand-note-data",
        {
          params: { applicationId, reviewId, isThereAConst },
        }
      ),
  });
}

export function useSaveDemandNoteDataForLevel3() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
      isThereAConst: boolean;
    }) =>
      _axios.post(
        "level-3-application-approval/save-demand-note-data",
        data,
        {
          responseType: "blob", // 🔑 THIS IS REQUIRED
        }
      ),
  });
}

export function useForwardApplicationForLevel3() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      reviewId: number;
      isThereAConst: boolean;
    }) =>
      _axios.post<{ success: true }>(
        "level-3-application-approval/approve-application",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}
